/**
 * Канонические дата/время-утилиты форм ЭДО. Раньше эти функции были скопированы по репо
 * (processes.tsx, DateField/TimeField, leaveForms, timeOffHours) — здесь один источник правды.
 */

/** Формат даты `YYYY-MM-DD`. */
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
/** Формат времени `HH:MM` (00:00–23:59). */
export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/** Число → строка с ведущим нулём (2 знака). */
export const pad2 = (n: number): string => String(n).padStart(2, '0')

/** `YYYY-MM-DD` + n дней (горизонт запроса производственного календаря). */
export function ymdPlusDays(s: string, days: number): string {
  const [y, m, d] = s.split('-').map(Number)
  const dt = new Date(y, m - 1, d + days)
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
}

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/
/**
 * `YYYY-MM-DD` + N лет. Нужно для автоподстановки плановой даты окончания ухода за ребёнком
 * (ТЗ-2026-013 §3.2: дата рождения + 3 года). Считаем по компонентам строки, а не через Date:
 * toISOString() на UTC+4/+5 съедает день (те же грабли, что у ymdPlusDays в форме командировки).
 * 29 февраля в невисокосном году схлопывается в 28-е — календарной даты 29-го там просто нет.
 * Некорректный вход → пустая строка: форма тогда ничего не подставляет.
 */
export function plusYearsYmd(ymd: string, years: number): string {
  const m = YMD_RE.exec(ymd)
  if (!m) return ''
  const [, y, mo, d] = m
  const year = Number(y) + years
  const month = Number(mo)
  const day = Number(d)
  // Последний день целевого месяца: day=0 следующего месяца (UTC — без сдвига таймзоны).
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const safeDay = Math.min(day, lastDay)
  return `${year}-${String(month).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`
}

/** Дата `YYYY-MM-DD` + время `HH:MM` (местное) → ISO 8601 UTC (с Z), либо null при невалидном вводе. */
export function toIsoUtc(date: string, time: string): string | null {
  if (!DATE_RE.test(date) || !TIME_RE.test(time)) return null
  const d = new Date(`${date}T${time}:00`) // no offset → parsed as device-local
  return isNaN(d.getTime()) ? null : d.toISOString()
}

/** `HH:MM` → минуты от полуночи. Вход уже провалидирован колесом времени. */
export const hmToMin = (s: string): number => {
  const [h, m] = s.split(':').map(Number)
  return h * 60 + m
}

/** минуты от полуночи → `HH:MM` (с ведущими нулями). */
export const minToHm = (n: number): string => `${pad2(Math.floor(n / 60))}:${pad2(n % 60)}`
